import { type CardDef, COMPONENT_CARD_KEYS, type ComponentCardKey, isValidLatLng } from '@shared/types';
import { cloneDeep, uuid } from '@shared/utils/core/general';
import type { LiteralUnion } from 'type-fest';

/**
 * Characters that may legitimately appear in a Joi expression string
 * (e.g. `string().min(3).required()`, `number().valid(1,2)`, `{#0}` refs).
 * Used for a non-executing sanity check — see the validationSchema note below.
 */
const VALID_JOI_EXPRESSION = /^[\w\s.,'"`()[\]{}#:+\-*/?=<>!&|$%]*$/;

/** Config keys each component understands — used to warn on mismatched authoring. */
const COMPONENT_CARD_CONFIG_KEYS: Record<ComponentCardKey, readonly string[]> = {
    VEHICLE_DAMAGE_PICKER: ['minPhotosPerPanel', 'panels', 'requiredPhotos'],
    INCIDENT_LOCATION_PICKER: ['defaultCenter', 'defaultZoom'],
    NEARBY_REPAIRERS: ['radiusKm', 'maxResults'],
    VEHICLE_INSPECTION: ['allowSkip', 'collectVehicleDetails', 'linkExpiryHours', 'inspectionMode', 'allowImageUpload'],
    CAPTURE_METHOD_SELECTOR: ['scanNextCardId', 'uploadNextCardId'],
};

export interface QuestionnaireIssue {
    severity: 'error' | 'warning';
    cardId?: string;
    childId?: string;
    message: string;
}

/**
 * Normalises a questionnaire definition without validating it: strips empty/legacy
 * keys, coerces id/boolean shapes, autofills missing child ids and numeric order.
 * Returns a deep clone — the input is never mutated.
 */
export function cleanQuestionnaire(cards: CardDef[]): CardDef[] {
    const _cards = cloneDeep(cards);
    _cards.forEach((c) => {
        //@ts-expect-error Typescript doesn't know about undefined
        delete c['undefined'];
        deleteIfEmpty(c, 'nextCardId');
        deleteIfEmpty(c, 'content');
        deleteIfEmpty(c, 'logicalGroup1');
        deleteIfEmpty(c, 'logicalGroup2');
        deleteIfEmpty(c, 'logicalGroup3');
        deleteIfEmpty(c, 'logicalGroup4');
        deleteIfEmpty(c, 'logicalGroup5');
        deleteIfEmpty(c, 'titleSystem');
        deleteIfEmpty(c, 'titleInsured');
        deleteIfEmpty(c, 'internalName');
        deleteIfEmpty(c, 'isRoot');
        deleteIfEmpty(c, 'required');
        deleteIfEmpty(c, 'nextCardCondition');
        deleteIfEmpty(c, 'nextCardId2');
        deleteIfEmpty(c, 'nextCard2Condition');
        deleteIfEmpty(c, 'nextCardId3');
        deleteIfEmpty(c, 'nextCard3Condition');
        deleteIfEmpty(c, 'sectionId');
        deleteIfEmpty(c, 'moduleId');
        deleteIfEmpty(c, 'component');
        deleteIfEmpty(c, 'componentConfig');
        toBooleanIfExist(c, 'required');
        toBooleanIfExist(c, 'isRoot');
        toStringIfExist(c, 'id');
        toStringIfExist(c, 'nextCardId');
        return c;
    });

    _cards
        // Array.isArray guard: imported/JSON definitions are only shallow-validated, so
        // `children` may be null or non-array here — treat it as empty instead of crashing.
        .flatMap((c) => ('children' in c && Array.isArray(c.children) ? c.children : []))
        .forEach((c) => {
            //@ts-expect-error Typescript doesn't know about undefined
            delete c['undefined'];
            if (!c.id) {
                c.id = uuid();
            }
            deleteIfEmpty(c, 'nextCardId');
            deleteIfEmpty(c, 'additionalCardId');
            deleteIfEmpty(c, 'parentCardId');
            deleteIfEmpty(c, 'internalName');
            deleteIfEmpty(c, 'componentConfig');
            deleteIfEmpty(c, 'description');
            deleteIfEmpty(c, 'validationSchema');
            deleteIfEmpty(c, 'pds1Id');
            deleteIfEmpty(c, 'pds2Id');
            deleteIfEmpty(c, 'pds3Id');
            deleteIfEmpty(c, 'pds4Id');
            deleteIfEmpty(c, 'pdsId1');
            deleteIfEmpty(c, 'pdsId2');
            deleteIfEmpty(c, 'pdsId3');
            deleteIfEmpty(c, 'pdsId4');
            deleteIfEmpty(c, 'logicalGroup1');
            deleteIfEmpty(c, 'logicalGroup2');
            deleteIfEmpty(c, 'logicalGroup3');
            deleteIfEmpty(c, 'logicalGroup4');
            deleteIfEmpty(c, 'logicalGroup5');
            deleteIfEmpty(c, 'categories - DB');
            deleteIfEmpty(c, 'allowed files - DB');
            deleteIfEmpty(c, 'specifiedItemId');
            deleteIfEmpty(c, 'required');
            deleteIfEmptyArray(c, 'fileCategories');
            deleteIfEmptyArray(c, 'allowedFileTypes');
            deleteIfEmptyArray(c, 'options');
            toStringIfExist(c, 'id');
            toStringIfExist(c, 'nextCardId');
            toStringIfExist(c, 'additionalCardId');
            toStringIfExist(c, 'parentCardId');
            toBooleanIfExist(c, 'required');
            if (c.order) {
                c.order = Number(c.order);
            }
            return c;
        });

    return _cards;
}

/**
 * Collects every validation problem in a (cleaned) definition without throwing.
 * Issue order matches the original throw-first validator so that
 * `cleanAndValidateQuestionnaire` keeps throwing the same error for the same input;
 * editor-only checks (duplicate card ids, multiple roots, missing titles) come last.
 */
export function collectQuestionnaireIssues(cards: CardDef[]): QuestionnaireIssue[] {
    const issues: QuestionnaireIssue[] = [];
    const error = (message: string, cardId?: string, childId?: string) =>
        issues.push({ severity: 'error', message, cardId, childId });
    const warning = (message: string, cardId?: string, childId?: string) =>
        issues.push({ severity: 'warning', message, cardId, childId });

    cards.forEach((c) => {
        if (c.nextCardId && !cards.find((el) => el.id === c.nextCardId)) {
            error(
                `The card #${c.id} has invalid value in the next card field (${c.nextCardId}). The card with this ID doesn't exist.`,
                c.id,
            );
        }
        if (c.type === 'FORM' && !c.nextCardId) {
            warning(
                `The card #${c.id} is missing the next card ID. The next card ID is required for the FORM children, unless it's the last card in the questionnaire or the questionnaire branch.`,
                c.id,
            );
        }
        if (c.type === 'COMPONENT') {
            // Authored JSON may omit `component` despite the type requiring it —
            // widen so the runtime guard survives the keyed-union narrowing
            const componentKey: string | undefined = c.component;
            if (!componentKey) {
                error(`The card #${c.id} is of type COMPONENT and is missing the component name.`, c.id);
                return;
            }
            if (!(COMPONENT_CARD_KEYS as readonly string[]).includes(componentKey)) {
                warning(
                    `The card #${c.id} references an unknown component "${componentKey}". Known components: ${COMPONENT_CARD_KEYS.join(', ')}. The card will be skipped in the form until a matching component exists.`,
                    c.id,
                );
            }
            if (!c.nextCardId) {
                warning(
                    `The card #${c.id} is missing the next card ID. The next card ID is required for COMPONENT cards, unless it's the last card in the questionnaire or the questionnaire branch.`,
                    c.id,
                );
            }
            if (c.componentConfig !== undefined) {
                if (typeof c.componentConfig !== 'object' || Array.isArray(c.componentConfig)) {
                    error(`The card #${c.id} has an invalid component config. It must be a JSON object.`, c.id);
                    return;
                }
                // Authored as free-form JSON — validate against the declared component's keys
                const config = c.componentConfig as Record<string, unknown>;
                const allowedKeys = COMPONENT_CARD_CONFIG_KEYS[c.component as ComponentCardKey];
                if (allowedKeys) {
                    const unknownKeys = Object.keys(config).filter((key) => !allowedKeys.includes(key));
                    if (unknownKeys.length > 0) {
                        warning(
                            `The card #${c.id} component config contains keys that don't belong to ${c.component}: ${unknownKeys.join(', ')}. They will be ignored.`,
                            c.id,
                        );
                    }
                }
                if (c.component === 'VEHICLE_DAMAGE_PICKER') {
                    const minPhotos = config.minPhotosPerPanel;
                    if (
                        minPhotos !== undefined &&
                        (typeof minPhotos !== 'number' || !Number.isInteger(minPhotos) || minPhotos < 0)
                    ) {
                        error(
                            `The card #${c.id} has an invalid minPhotosPerPanel in the component config. It must be a non-negative integer.`,
                            c.id,
                        );
                    }
                }
                if (c.component === 'CAPTURE_METHOD_SELECTOR') {
                    for (const key of ['scanNextCardId', 'uploadNextCardId'] as const) {
                        const target = config[key];
                        if (target === undefined) {
                            continue;
                        }
                        if (typeof target !== 'string') {
                            error(
                                `The card #${c.id} has an invalid ${key} in the component config. It must be a card ID string.`,
                                c.id,
                            );
                        } else if (!cards.find((el) => el.id === target)) {
                            error(
                                `The card #${c.id} has an invalid value in ${key} (${target}). The card with this ID doesn't exist.`,
                                c.id,
                            );
                        }
                    }
                }
                if (c.component === 'INCIDENT_LOCATION_PICKER') {
                    const defaultCenter = config.defaultCenter as
                        | { latitude?: unknown; longitude?: unknown }
                        | null
                        | undefined;
                    if (
                        defaultCenter !== undefined &&
                        (defaultCenter === null ||
                            typeof defaultCenter !== 'object' ||
                            !isValidLatLng(defaultCenter.latitude, defaultCenter.longitude))
                    ) {
                        error(
                            `The card #${c.id} has an invalid defaultCenter in the component config. It must be an object with latitude (-90 to 90) and longitude (-180 to 180).`,
                            c.id,
                        );
                    }
                    const defaultZoom = config.defaultZoom;
                    if (defaultZoom !== undefined && (typeof defaultZoom !== 'number' || defaultZoom < 0)) {
                        error(
                            `The card #${c.id} has an invalid defaultZoom in the component config. It must be a non-negative number.`,
                            c.id,
                        );
                    }
                }
                if (c.component === 'VEHICLE_INSPECTION') {
                    const inspectionMode = config.inspectionMode;
                    if (inspectionMode !== undefined && inspectionMode !== 'FULL' && inspectionMode !== 'LITE') {
                        error(
                            `The card #${c.id} has an invalid inspectionMode in the component config. It must be either FULL or LITE.`,
                            c.id,
                        );
                    }
                    const allowImageUpload = config.allowImageUpload;
                    if (allowImageUpload !== undefined && typeof allowImageUpload !== 'boolean') {
                        error(
                            `The card #${c.id} has an invalid allowImageUpload in the component config. It must be a boolean.`,
                            c.id,
                        );
                    }
                }
            }
            if (componentKey === 'CAPTURE_METHOD_SELECTOR') {
                const config = (c.componentConfig ?? {}) as Record<string, unknown>;
                if (!config.scanNextCardId && !config.uploadNextCardId) {
                    warning(
                        `The card #${c.id} is a capture method selector with no branch targets configured — both choices will continue to the same next card.`,
                        c.id,
                    );
                }
            }
        }
    });

    const containsRootCard = cards.some((q) => q.isRoot);
    if (!containsRootCard) {
        error(`The questionnaire doesn't contain a root card. Exactly one root card is required (isRoot = true).`);
    }

    cards.forEach((card) => {
        if (!('children' in card) || !Array.isArray(card.children)) {
            return;
        }
        card.children.forEach((c) => {
            if (c.nextCardId && !cards.find((q) => q.id === c.nextCardId)) {
                error(
                    `The child #${c.id} is referencing a next card ID (#${c.nextCardId}) that doesn't exist.`,
                    card.id,
                    c.id,
                );
            }
            if (c.additionalCardId && !cards.find((q) => q.id === c.additionalCardId)) {
                error(
                    `The child #${c.id} is referencing an additional card ID (#${c.additionalCardId}) that doesn't exist.`,
                    card.id,
                    c.id,
                );
            }
            // NOTE: deliberately a non-executing syntactic check. The previous
            // implementation ran `new Function(processJoiStringSchema(...))()`,
            // which executes the schema string as arbitrary JS. That is unsafe
            // in this bulk pass because it runs on freshly *imported* (untrusted)
            // definitions at file-parse time. Author-time syntax feedback for a
            // child the user is actively editing still lives in ChildEditorDrawer.
            if (c.validationSchema && !VALID_JOI_EXPRESSION.test(c.validationSchema)) {
                warning(
                    `The child #${c.id} has a validation schema with unexpected characters. Check it in the field editor.`,
                    card.id,
                    c.id,
                );
            }
        });
    });

    cards.forEach((c) => {
        // Find if two ids are the same
        if ('children' in c && Array.isArray(c.children)) {
            const ids = c.children.map((o) => o.id);
            const hasDuplicates = new Set(ids).size !== ids.length;
            if (hasDuplicates) {
                error(
                    `The card #${c.id} has duplicated child IDs. Please make sure that all child IDs are unique.`,
                    c.id,
                );
            }
        }

        if (c.type === 'MULTI_SELECT' || c.type === 'SINGLE_SELECT') {
            if (!Array.isArray(c.children) || c.children.length === 0) {
                error(`The card #${c.id} is of type ${c.type} and it doesn't have any children.`, c.id);
            } else if (c.children.some((child) => child.type !== 'OPTION')) {
                error(`The card #${c.id} is of type ${c.type} and it has children that are not of type OPTION.`, c.id);
            }
        }
    });

    // Editor-grade checks below — kept after the legacy checks so the
    // throw-first wrapper keeps surfacing the same error it always has.
    const cardIdCounts = new Map<string, number>();
    cards.forEach((c) => cardIdCounts.set(c.id, (cardIdCounts.get(c.id) ?? 0) + 1));
    cardIdCounts.forEach((count, id) => {
        if (count > 1) {
            // Warning, not error: legacy questionnaires with duplicate card ids
            // run at runtime (nextCardId lookup takes the first match), so they
            // must stay saveable. The editor separately disables drag-reorder
            // while duplicates exist.
            warning(`The card ID #${id} is used by ${count} cards. Card IDs should be unique.`, id);
        }
    });

    const rootIds = cards.filter((c) => c.isRoot).map((c) => c.id);
    if (rootIds.length > 1) {
        warning(
            `The questionnaire has ${rootIds.length} root cards (${rootIds.join(', ')}). Exactly one root card is expected (isRoot = true).`,
        );
    }

    cards.forEach((c) => {
        if (!c.title) {
            warning(`The card #${c.id} is missing a title.`, c.id);
        }
    });

    return issues;
}

export function cleanAndValidateQuestionnaire(cards: CardDef[]): {
    valid: boolean;
    warnings: Array<{ title: string; message: string }>;
    cards: CardDef[];
} {
    const _cards = cleanQuestionnaire(cards);
    const issues = collectQuestionnaireIssues(_cards);

    const firstError = issues.find((issue) => issue.severity === 'error');
    if (firstError) {
        throw new Error(firstError.message);
    }

    return {
        valid: true,
        warnings: issues
            .filter((issue) => issue.severity === 'warning')
            .map((issue) => ({ title: 'Warning', message: issue.message })),
        cards: _cards,
    };
}

function deleteIfEmpty<T extends Record<string, any>>(obj: T, property: LiteralUnion<keyof T, string>) {
    if (!obj[property]) {
        delete obj[property];
    }
}

function deleteIfEmptyArray<T extends Record<string, any>>(obj: T, property: LiteralUnion<keyof T, string>) {
    const propertyValue = obj[property];
    if (propertyValue && Array.isArray(propertyValue) && propertyValue.length === 0) {
        delete obj[property];
    }
}

function toStringIfExist<T extends Record<string, any>>(obj: T, property: LiteralUnion<keyof T, string>) {
    if (obj[property]) {
        obj[property] = obj[property].toString().trim();
    }
}

function toBooleanIfExist<T extends Record<string, any>>(obj: T, property: LiteralUnion<keyof T, string>) {
    if (obj[property]) {
        obj[property] = Boolean(obj[property]) as any;
    }
}
